import {
  GlobalOutlined,
  LogoutOutlined,
  OrderedListOutlined,
  ShareAltOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { history, setLocale, useModel, useRequest } from '@umijs/max';
import { Avatar, Button, Popover, Segmented, message } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { loginModal } from '@/components/LoginModal';
import { paymentModal } from '@/components/PaymentModal';
import {
  DASHBOARD_DATA_CACHE_KEY,
  extractDashboardLedgerBalance,
  fetchDashboardData,
} from '@/services/dashboard';
import { outLogin } from '@/services/auth';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';

const LANGUAGE_LABELS = {
  'zh-CN': '中',
  'en-US': 'EN',
} as const;

const useStyles = createStyles(({ token, css }) => {
  const borderColor = `color-mix(in srgb, ${token.colorBorderSecondary} 88%, ${token.colorBgContainer})`;
  const footerBackground = 'transparent';
  const hoverBackground = `color-mix(in srgb, ${token.colorFillSecondary} 45%, ${token.colorBgContainer})`;
  const subtleBackground = `color-mix(in srgb, ${token.colorBgContainer} 84%, ${token.colorFillQuaternary})`;
  const focusRing = `0 0 0 2px color-mix(in srgb, ${token.colorPrimaryBg} 75%, transparent)`;
  const elevatedSurface = css`
    background:
      linear-gradient(
        180deg,
        color-mix(in srgb, ${token.colorBgContainer} 95%, ${subtleBackground}) 0%,
        color-mix(in srgb, ${subtleBackground} 96%, ${token.colorBgContainer}) 100%
      );
    box-shadow:
      0 18px 32px -30px rgba(15, 23, 42, 0.28),
      inset 0 1px 0 color-mix(in srgb, white 55%, transparent);
  `;

  return {
    root: css`
      width: 100%;
      margin: 0;
      padding: 0;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 84%, transparent);
      background: ${footerBackground};
      overflow: hidden;
    `,
    collapsedRoot: css`
      padding: 10px 0 12px;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 84%, transparent);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: ${footerBackground};
    `,
    collapsedUserBadge: css`
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: transparent;
      box-shadow: none;
    `,
    footerPanel: css`
      display: flex;
      flex-direction: column;
      background:
        linear-gradient(
          180deg,
          ${token.colorBgContainer} 0%,
          color-mix(in srgb, ${subtleBackground} 92%, ${token.colorBgContainer}) 100%
        );
    `,
    accountHeaderRow: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      width: 100%;
      min-width: 0;
      padding: 0;
      box-sizing: border-box;
    `,
    guestSummaryRow: css`
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 12px;
      width: 100%;
      min-width: 0;
      padding: 0;
      box-sizing: border-box;
    `,
    footerShellCard: css`
      ${elevatedSurface};
      overflow: hidden;
      border-radius: 0;
      padding: 4px;
    `,
    accountHeaderActionButton: css`
      height: 32px;
      padding-inline: 14px;
      border: 1px solid color-mix(
        in srgb,
        ${token.colorBorderSecondary} 84%,
        ${token.colorBgContainer}
      );
      border-radius: 999px;
      background: color-mix(
        in srgb,
        ${token.colorBgContainer} 72%,
        ${token.colorFillQuaternary}
      );
      color: ${token.colorTextSecondary};
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
      box-shadow: none;
      flex-shrink: 0;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        color: ${token.colorText};
        border-color: color-mix(
          in srgb,
          ${token.colorPrimaryBorder} 58%,
          ${token.colorBorderSecondary}
        );
        background: color-mix(in srgb, ${hoverBackground} 78%, ${token.colorBgContainer});
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    guestAuthActionButton: css`
      width: 100%;
      min-width: 88px;
      height: 36px;
      padding-inline: 18px;
      justify-content: center;
      flex-shrink: 0;
      border-color: ${token.colorPrimary};
      border-radius: 10px;
      background: ${token.colorPrimary};
      color: ${token.colorWhite};
      font-size: 15px;
      font-weight: 600;
      letter-spacing: 0.01em;
      box-shadow: none;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        box-shadow 0.2s ease;

      &, &.ant-btn-primary {
        background: ${token.colorPrimary};
        border-color: ${token.colorPrimary};
        color: ${token.colorWhite};
        box-shadow: none;
      }

      &.ant-btn-primary:not(:disabled):hover,
      &.ant-btn-primary:not(:disabled):focus-visible {
        background: ${token.colorPrimaryHover};
        border-color: ${token.colorPrimaryHover};
        color: ${token.colorWhite};
        box-shadow: none;
      }

      &.ant-btn-primary:not(:disabled):active {
        background: ${token.colorPrimaryActive};
        border-color: ${token.colorPrimaryActive};
        color: ${token.colorWhite};
        box-shadow: none;
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    accountCard: css`
      width: 100%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 14px 12px 10px;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 82%, transparent);
      background:
        linear-gradient(
          180deg,
          color-mix(in srgb, ${token.colorPrimaryBg} 16%, transparent) 0%,
          transparent 100%
        );
      box-sizing: border-box;
    `,
    accountCardTail: css`
      margin-top: 0;
    `,
    accountLead: css`
      display: flex;
      align-items: center;
      gap: 9px;
      min-width: 0;
      flex: 1;
    `,
    accountMeta: css`
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      text-align: left;
    `,
    accountName: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 15px;
      line-height: 21px;
      font-weight: 600;
      color: ${token.colorTextHeading};
    `,
    accountSubtitle: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
      line-height: 16px;
      color: ${token.colorTextSecondary};
    `,
    expandedAvatar: css`
      background: ${token.colorPrimary};
      flex-shrink: 0;
    `,
    collapsedButton: css`
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: ${token.colorTextSecondary};
      cursor: pointer;
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    collapsedAvatar: css`
      background: ${token.colorPrimary};
    `,
    collapsedPinnedActionButton: css`
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: ${token.colorTextSecondary};
      cursor: pointer;
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    localePopoverBody: css`
      width: 240px;
      padding: 6px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
    collapsedAccountPopoverBody: css`
      width: 220px;
      padding: 6px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
    collapsedAccountPanel: css`
      ${elevatedSurface};
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 10px;
      border-radius: 16px;
    `,
    collapsedAccountHeader: css`
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    `,
    collapsedAccountMeta: css`
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    collapsedAccountName: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 14px;
      line-height: 20px;
      font-weight: 600;
      color: ${token.colorTextHeading};
    `,
    collapsedAccountSubtitle: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
      line-height: 16px;
      color: ${token.colorTextSecondary};
    `,
    collapsedAccountLogoutButton: css`
      width: 100%;
      height: 32px;
      justify-content: center;
      border-radius: 999px;
      box-shadow: none;
      font-size: 12px;
      font-weight: 500;
    `,
    sectionDivider: css`
      width: 100%;
      height: 1px;
      background: color-mix(in srgb, ${borderColor} 90%, transparent);
    `,
    balancePanel: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    balanceMeta: css`
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    balanceLabel: css`
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextSecondary};
    `,
    balanceValue: css`
      font-size: 18px;
      line-height: 24px;
      font-weight: 600;
      color: ${token.colorTextHeading};
      letter-spacing: -0.02em;
    `,
    rechargeButton: css`
      height: 32px;
      padding-inline: 12px;
      border-radius: 999px;
      box-shadow: none;
      font-size: 12px;
      font-weight: 500;
    `,
    sectionTitle: css`
      padding: 2px 8px 0;
      font-size: 11px;
      line-height: 18px;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: ${token.colorTextTertiary};
    `,
    settingBlock: css`
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    settingRow: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    `,
    settingBlockEmbedded: css`
      padding: 8px 6px;
      border-radius: 0;
      background: transparent;
    `,
    settingRowEmbedded: css`
      gap: 16px;
    `,
    settingLead: css`
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      text-align: left;
      line-height: 20px;
      color: ${token.colorText};
    `,
    settingIcon: css`
      width: 16px;
      height: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      line-height: 1;
      color: ${token.colorTextSecondary};
      font-size: 14px;
    `,
    embeddedSegmented: css`
      padding: 2px;
      border-radius: 12px;
      background: color-mix(in srgb, ${token.colorFillSecondary} 70%, ${token.colorBgContainer});
      box-shadow: inset 0 0 0 1px color-mix(in srgb, ${token.colorBorderSecondary} 75%, transparent);

      .ant-segmented-item {
        min-width: 38px;
        border-radius: 10px;
      }

      .ant-segmented-item-label {
        padding-inline: 10px;
        font-size: 12px;
      }
    `,
    actionList: css`
      display: flex;
      flex-direction: column;
      gap: 4px;
    `,
    actionPanel: css`
      padding: 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    actionButton: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 40px;
      padding: 0 10px;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: ${token.colorText};
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }
    `,
    actionLead: css`
      display: inline-flex;
      align-items: center;
      text-align: left;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      line-height: 20px;
    `,
    pinnedActionLead: css`
      display: inline-flex;
      align-items: center;
      text-align: left;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      line-height: 20px;
    `,
    pinnedActionLabel: css`
      display: block;
      min-width: 0;
      line-height: 20px;
    `,
    actionMeta: css`
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextSecondary};
      font-variant-numeric: tabular-nums;
      letter-spacing: -0.01em;
    `,
    loggedInPanel: css`
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 12px;
    `,
    loggedInActionSection: css`
      & + & {
        border-top: 1px solid color-mix(in srgb, ${borderColor} 82%, transparent);
      }
    `,
    pinnedActionSection: css`
      border-top: 1px solid color-mix(in srgb, ${borderColor} 90%, transparent);
    `,
    pinnedActionButton: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 48px;
      padding: 10px 12px;
      border: none;
      background: transparent;
      color: ${token.colorText};
      cursor: pointer;
      transition:
        background-color 0.2s ease,
        box-shadow 0.2s ease,
        transform 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:active {
        transform: translateY(0.5px);
      }

      &:focus-visible {
        outline: none;
        box-shadow: inset ${focusRing};
      }
    `,
    localeInlineRow: css`
      width: 100%;
      min-height: 48px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 12px;
      border-radius: 14px;
      background: transparent;
      box-sizing: border-box;
    `,
    localeInlineLead: css`
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      line-height: 20px;
      color: ${token.colorText};
    `,
    localeInlineSegmented: css`
      padding: 3px;
      border-radius: 999px;
      background: color-mix(
        in srgb,
        ${token.colorFillSecondary} 78%,
        ${token.colorBgContainer}
      );
      box-shadow:
        inset 0 0 0 1px color-mix(in srgb, ${token.colorBorderSecondary} 72%, transparent),
        0 6px 14px -12px rgba(15, 23, 42, 0.28);

      .ant-segmented-item {
        min-width: 40px;
        border-radius: 999px;
      }

      .ant-segmented-item-label {
        padding-inline: 10px;
        font-size: 12px;
      }
    `,
  };
});

type FooterActionItem = {
  key: string;
  label: string;
  icon: React.ElementType;
  onClick: () => void | Promise<void>;
};

const SiderFooter: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { styles } = useStyles();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [localePopoverOpen, setLocalePopoverOpen] = React.useState(false);
  const [collapsedAccountPopoverOpen, setCollapsedAccountPopoverOpen] =
    React.useState(false);

  const isLoggedIn = !!initialState?.currentUser;
  const user = initialState?.currentUser?.user;
  const affCodeInfo = initialState?.affCodeInfo;
  const userId = user?.id;
  const currentLocale = getAppLocale();

  const { data: dashboardData } = useRequest(
    () => fetchDashboardData({ skipErrorHandler: true }),
    {
      ready: Boolean(userId),
      cacheKey: DASHBOARD_DATA_CACHE_KEY,
      staleTime: 5_000,
      refreshOnWindowFocus: false,
    },
  );
  const balance = React.useMemo(() => {
    if (!userId) {
      return 0;
    }
    return extractDashboardLedgerBalance(dashboardData);
  }, [dashboardData, userId]);

  const balanceText = React.useMemo(() => {
    const amount = Number(balance || 0);
    if (!Number.isFinite(amount)) {
      return '0';
    }

    return new Intl.NumberFormat(currentLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, [balance, currentLocale]);

  /**
   * 登录态卡片需要沿用原来的“项目/空间名 + 用户名”展示语义：
   * - 第一行展示当前归属空间，帮助用户先确认自己正在哪个 partner 下工作；
   * - 第二行再展示真实用户名，而不是账号字段，避免邮箱/登录账号过长时破坏信息层级。
   */
  const userName =
    initialState?.currentUser?.partner?.title ||
    t('components.siderFooter.notLoggedIn', 'Not signed in');
  const accountSubtitle =
    user?.name ||
    t('components.siderFooter.personalAccount', 'Personal account');
  const guestAuthActionLabel = t(
    'components.topNavAccount.loginOrRegister',
    'Login / Register',
  );

  const handleRecharge = (defaultTab: 'plans' | 'recharge' = 'recharge') => {
    paymentModal.show({ defaultTab });
  };

  const loginOut = async () => {
    localStorage.removeItem('token');
    await outLogin();
    message.success(
      t('components.siderFooter.logoutSuccess', 'Logged out successfully'),
    );
    history.replace('/home');
  };

  const handleLogout = async () => {
    flushSync(() => {
      setInitialState((state) => ({ ...state, currentUser: undefined }));
    });

    await loginOut();
  };

  const handleLocaleChange = (value: string | number) => {
    const nextLocale = String(value);
    if (nextLocale !== currentLocale) {
      setLocalePopoverOpen(false);
      setLocale(nextLocale, false);
    }
  };

  /**
   * “邀请链接”需要兼容两类复制环境：
   * 1. 新浏览器下优先走 `navigator.clipboard.writeText`；
   * 2. 某些 HTTP / 内嵌 WebView 场景下 `clipboard` 为空，此时退回旧版 `execCommand('copy')`，
   *    避免用户点击入口直接触发运行时异常。
   */
  const copyTextToClipboard = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.style.pointerEvents = 'none';
    textarea.style.left = '-9999px';

    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);

    const copied =
      typeof document.execCommand === 'function' &&
      document.execCommand('copy');

    document.body.removeChild(textarea);

    if (!copied) {
      throw new Error('copy_unavailable');
    }
  };

  const handleCopyInviteLink = async () => {
    if (!affCodeInfo?.affLink) return;

    try {
      await copyTextToClipboard(affCodeInfo.affLink);
      message.success(
        t(
          'components.siderFooter.inviteLinkCopied',
          'Invite link copied to clipboard',
        ),
      );
    } catch {
      message.error(
        t(
          'components.siderFooter.copyFailed',
          'Copy failed, please copy it manually',
        ),
      );
    }
  };

  const handleOpenTransactionHistory = () => {
    history.push('/payment/orders');
  };

  /**
   * 账户动作统一在这里组装。
   * 这样展开态和折叠态都消费同一份配置，避免把某个入口只留在旧弹层里。
   */
  const accountActions: FooterActionItem[] = [];
  accountActions.push({
    key: 'transaction-history',
    label: t(
      'components.siderFooter.transactionHistory',
      'Transaction History',
    ),
    icon: OrderedListOutlined,
    onClick: handleOpenTransactionHistory,
  });
  if (affCodeInfo?.affLink) {
    accountActions.push({
      key: 'invite-link',
      label: t('components.siderFooter.inviteLink', 'Invite Link'),
      icon: ShareAltOutlined,
      onClick: handleCopyInviteLink,
    });
  }
  accountActions.push({
    key: 'logout',
    label: t('components.siderFooter.logout', 'Logout'),
    icon: LogoutOutlined,
    onClick: handleLogout,
  });

  /**
   * 登录态展开区已经把“退出登录”提升成右上角主按钮，
   * 所以下方列表只保留需要继续平铺展示的动作，避免用户在同一块面板里看到重复入口。
   */
  const expandedListActions = accountActions.filter(
    (action) => action.key !== 'logout',
  );

  /**
   * 语言偏好不能依赖登录态。
   * 游客第一次进入站点时，也需要在不打开登录弹窗的前提下把界面切到可读语言；
   * 但“偏好设置”标题只在登录态账户面板里有意义，否则会让游客误以为还有更多配置项可用。
   */
  const renderLocalePreferenceBlock = ({
    showTitle = true,
    embedded = false,
  }: {
    showTitle?: boolean;
    embedded?: boolean;
  } = {}) => (
    <>
      {showTitle && (
        <div className={styles.sectionTitle}>
          {t('components.siderFooter.preferences', 'Preferences')}
        </div>
      )}
      <div
        className={[
          styles.settingBlock,
          embedded ? styles.settingBlockEmbedded : '',
        ].join(' ')}
      >
        <div
          className={[
            styles.settingRow,
            embedded ? styles.settingRowEmbedded : '',
          ].join(' ')}
        >
          <span className={styles.settingLead}>
            <GlobalOutlined className={styles.settingIcon} />
            <span>{t('components.siderFooter.language', 'Language')}</span>
          </span>
          <Segmented
            className={embedded ? styles.embeddedSegmented : undefined}
            value={currentLocale}
            onChange={handleLocaleChange}
            options={Object.entries(LANGUAGE_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </div>
      </div>
    </>
  );

  /**
   * “订阅与充值”是侧栏底部的固定主操作，不应依赖登录态之外的临时展开容器。
   * 登录态里它和用户入口需要保持同一视觉节奏，避免像两个不相关的模块；
   * 展开态使用带分割线的列表项，而不是独立胶囊按钮，这样能更自然地挂在设置区下面。
   */
  const renderPinnedSubscriptionAction = ({
    compact = false,
  }: {
    compact?: boolean;
  } = {}) => {
    const actionLabel = t(
      'components.siderFooter.subscriptionAndRecharge',
      'Subscription & Recharge',
    );

    if (compact) {
      return (
        <button
          type="button"
          className={styles.collapsedPinnedActionButton}
          onClick={() => handleRecharge('plans')}
          title={actionLabel}
          aria-label={actionLabel}
        >
          <WalletOutlined style={{ fontSize: 16 }} />
        </button>
      );
    }

    return (
      <button
        type="button"
        className={styles.pinnedActionButton}
        onClick={() => handleRecharge('plans')}
      >
        <span className={styles.pinnedActionLead}>
          <WalletOutlined className={styles.settingIcon} />
          <span className={styles.pinnedActionLabel}>{actionLabel}</span>
        </span>
        <span className={styles.actionMeta}>
          {t('components.siderFooter.balanceValue', '{amount}', {
            amount: balanceText,
          })}
        </span>
      </button>
    );
  };

  /**
   * 登录态展开区改成和游客态相同的行式列表后，
   * 其它账户动作也需要复用同一套行高、分割线与点击反馈，避免 footer 里出现两种风格。
   */
  const renderExpandedActionRow = (action: FooterActionItem) => {
    const Icon = action.icon;
    return (
      <button
        key={action.key}
        type="button"
        className={styles.pinnedActionButton}
        onClick={action.onClick}
      >
        <span className={styles.pinnedActionLead}>
          <Icon className={styles.settingIcon} />
          <span className={styles.pinnedActionLabel}>{action.label}</span>
        </span>
      </button>
    );
  };

  /**
   * 登录态的语言切换现在进入统一动作卡。
   * 这里不再复用游客态的“设置块”外壳，避免 footer 下半部分出现列表行和胶囊块两套视觉语言。
   */
  const renderFooterLocaleRow = () => (
    <div className={styles.localeInlineRow}>
      <span className={styles.localeInlineLead}>
        <GlobalOutlined className={styles.settingIcon} />
        <span>{t('components.siderFooter.language', 'Language')}</span>
      </span>
      <Segmented
        className={styles.localeInlineSegmented}
        value={currentLocale}
        onChange={handleLocaleChange}
        options={Object.entries(LANGUAGE_LABELS).map(([value, label]) => ({
          value,
          label,
        }))}
      />
    </div>
  );

  /**
   * 登录态展开 footer 改成“先动作、后身份”的信息顺序：
   * 1. 先放交易/邀请等高频入口，减少用户为了执行操作还要越过账户卡片的来回视线跳转；
   * 2. 再放订阅与语言，这两项属于账户级偏好和资金能力；
   * 3. 最后才落到用户名，让当前身份成为结尾确认信息，而不是抢占顶部首屏。
   */
  const renderLoggedInFooterSections = () => (
    <>
      {expandedListActions.map((action) => (
        <div key={action.key} className={styles.loggedInActionSection}>
          {renderExpandedActionRow(action)}
        </div>
      ))}
      <div className={styles.loggedInActionSection}>
        {renderPinnedSubscriptionAction()}
      </div>
      <div className={styles.loggedInActionSection}>
        {renderFooterLocaleRow()}
      </div>
    </>
  );

  /**
   * 游客态只展示对游客可用的动作，但仍然保持和登录态同一套层级：
   * “订阅与充值”与“语言”沿用登录态动作行，底部再落到登录引导摘要，
   * 这样游客与已登录用户在 footer 里看到的是同一种产品结构，而不是两套独立模块。
   */
  const renderGuestFooterSections = () => (
    <>
      <div className={styles.loggedInActionSection}>
        {renderPinnedSubscriptionAction()}
      </div>
      <div className={styles.loggedInActionSection}>
        {renderFooterLocaleRow()}
      </div>
    </>
  );

  const localePopoverContent = (
    <div className={styles.localePopoverBody}>
      {renderLocalePreferenceBlock({ showTitle: false })}
    </div>
  );

  const renderCollapsedActionButton = (action: FooterActionItem) => {
    const Icon = action.icon;
    return (
      <button
        key={action.key}
        type="button"
        className={styles.collapsedButton}
        onClick={action.onClick}
        title={action.label}
        aria-label={action.label}
      >
        <Icon style={{ fontSize: 16 }} />
      </button>
    );
  };

  const collapsedAccountPopoverContent = (
    <div className={styles.collapsedAccountPopoverBody}>
      <div className={styles.collapsedAccountPanel}>
        <div className={styles.collapsedAccountHeader}>
          <Avatar
            size={28}
            icon={<UserOutlined />}
            src={user?.avatar}
            className={styles.collapsedAvatar}
          />
          <div className={styles.collapsedAccountMeta}>
            <div className={styles.collapsedAccountName}>{userName}</div>
            <div className={styles.collapsedAccountSubtitle}>{accountSubtitle}</div>
          </div>
        </div>
        {/**
         * 折叠态把头像和退出入口合并成一个触发器后，
         * 需要在弹层内补回“当前是谁 + 如何退出”的最小闭环，避免用户只能看见头像却找不到退出动作。
         */}
        <Button
          className={styles.collapsedAccountLogoutButton}
          onClick={() => {
            setCollapsedAccountPopoverOpen(false);
            void handleLogout();
          }}
        >
          {t('components.siderFooter.logout', 'Logout')}
        </Button>
      </div>
    </div>
  );

  if (collapsed) {
    return (
      <div className={styles.collapsedRoot}>
        {isLoggedIn ? (
          <>
            {/**
             * 折叠态需要和展开态保持同一套信息顺序：
             * - 先是交易/邀请这类高频动作；
             * - 再是订阅与语言这类账户能力；
             * - 最后只保留头像触发器，把身份与退出合并进弹层里，避免底部出现两个意义高度耦合的图标。
             */}
            {expandedListActions.map(renderCollapsedActionButton)}
            {renderPinnedSubscriptionAction({ compact: true })}
            <Popover
              trigger="click"
              placement="topRight"
              open={localePopoverOpen}
              onOpenChange={setLocalePopoverOpen}
              content={localePopoverContent}
              styles={{ body: { padding: 0 } }}
            >
              <button
                type="button"
                className={styles.collapsedButton}
                title={t('components.siderFooter.language', 'Language')}
                aria-label={t('components.siderFooter.language', 'Language')}
              >
                <GlobalOutlined style={{ fontSize: 16 }} />
              </button>
            </Popover>
            <Popover
              trigger="click"
              placement="topRight"
              open={collapsedAccountPopoverOpen}
              onOpenChange={setCollapsedAccountPopoverOpen}
              content={collapsedAccountPopoverContent}
              styles={{ body: { padding: 0 } }}
            >
              <button
                type="button"
                className={styles.collapsedButton}
                title={userName}
                aria-label={userName}
              >
                <div className={styles.collapsedUserBadge}>
                  <Avatar
                    size={22}
                    icon={<UserOutlined />}
                    src={user?.avatar}
                    className={styles.collapsedAvatar}
                  />
                </div>
              </button>
            </Popover>
          </>
        ) : (
          <>
            {renderPinnedSubscriptionAction({ compact: true })}
            <Popover
              trigger="click"
              placement="topRight"
              open={localePopoverOpen}
              onOpenChange={setLocalePopoverOpen}
              content={localePopoverContent}
              styles={{ body: { padding: 0 } }}
            >
              <button
                type="button"
                className={styles.collapsedButton}
                title={t('components.siderFooter.language', 'Language')}
                aria-label={t('components.siderFooter.language', 'Language')}
              >
                <GlobalOutlined style={{ fontSize: 16 }} />
              </button>
            </Popover>
            <button
              type="button"
              className={styles.collapsedButton}
              onClick={() => loginModal.show()}
              title={guestAuthActionLabel}
              aria-label={guestAuthActionLabel}
            >
              <UserOutlined style={{ fontSize: 16 }} />
            </button>
          </>
        )}
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className={styles.root}>
        <div className={styles.footerPanel}>
          {/**
           * 游客态沿用和登录态相同的外壳与底部摘要结构，
           * 避免“游客是提示卡 + 功能列表、登录后是账户面板”这种割裂观感。
           */}
          <div className={styles.footerShellCard}>
            {renderGuestFooterSections()}
            <div className={[styles.accountCard, styles.accountCardTail].join(' ')}>
              <div className={styles.guestSummaryRow}>
                {/**
                 * 游客态底部摘要只保留一个“登录/注册”动作。
                 * 订阅与语言已经在上方列表里各自成行，继续叠加说明文案会让 footer 重新分裂成“动作区 + 提示卡”两套层级，
                 * 也与这次只展示单一登录入口的交互目标不一致。
                 */}
                <Button
                  type="primary"
                  className={styles.guestAuthActionButton}
                  onClick={() => loginModal.show()}
                  aria-label={guestAuthActionLabel}
                >
                  {guestAuthActionLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.footerPanel}>
        {/**
         * 登录态 footer 合并成一张连续卡片，
         * 让“常用动作”和“当前身份”共享同一外壳，避免看起来像两个临时拼接的模块。
         */}
        <div className={styles.footerShellCard}>
          {renderLoggedInFooterSections()}
          <div className={[styles.accountCard, styles.accountCardTail].join(' ')}>
            <div className={styles.accountHeaderRow}>
              <span className={styles.accountLead}>
                <Avatar
                  size={30}
                  icon={<UserOutlined />}
                  src={user?.avatar}
                  className={styles.expandedAvatar}
                />
                <span className={styles.accountMeta}>
                  <span className={styles.accountName}>{userName}</span>
                  <span className={styles.accountSubtitle}>{accountSubtitle}</span>
                </span>
              </span>
              {/**
               * 用户名块现在移动到 footer 末尾后，
               * 这里继续保留“退出登录”作为伴随动作，让“当前是谁”和“如何退出”仍然在同一块里闭环。
               */}
              <Button
                className={styles.accountHeaderActionButton}
                onClick={handleLogout}
              >
                {t('components.siderFooter.logout', 'Logout')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiderFooter;
